/**
 * receiptPdf.js
 *
 * PDF download for order receipts.
 *
 * Calls GET /api/orders/:orderId/receipt/pdf (Bearer auth required).
 * Backend returns binary PDF with:
 *   Content-Type: application/pdf
 *   Content-Disposition: attachment; filename="receipt-PT-YYYY-NNNNNN.pdf"
 *
 * The browser's native file-save dialog is triggered via a temporary
 * anchor element. No third-party library required on the frontend.
 *
 * Backend spec: docs/backend/RECEIPT_BACKEND.md §2
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Fetch and trigger browser download of the receipt PDF.
 *
 * @param {string} orderId  - The order ID (URL param :id)
 * @param {string} idToken  - Firebase ID token from getIdToken()
 * @throws {Error}          - If the request fails (non-2xx response)
 */
export const downloadReceiptPdf = async (orderId, idToken) => {
  const res = await fetch(`${API_URL}/api/orders/${orderId}/receipt/pdf`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? err.message ?? `PDF download failed: ${res.status}`);
  }

  // Derive filename from Content-Disposition header if present,
  // otherwise fall back to a sensible default.
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `receipt-${orderId}.pdf`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Release object URL after a short delay to allow the download to start
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};
